import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  Property,
  PropertyFilters,
  PropertySearchParams,
} from "@/services/types/property";
import { propertiesApi } from "@/services/api/properties";

interface PropertyState {
  properties: Property[];
  featuredProperties: Property[];
  savedProperties: Property[];
  savedPropertiesLoaded: boolean;
  currentProperty: Property | null;
  filters: PropertyFilters;
  searchParams: PropertySearchParams;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const initialState: PropertyState = {
  properties: [],
  featuredProperties: [],
  savedProperties: [],
  savedPropertiesLoaded: false,
  currentProperty: null,
  filters: {},
  searchParams: {
    page: 1,
    limit: 12,
    sortBy: "createdAt",
    sortOrder: "desc",
  },
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  },
};

// Async thunks
export const fetchProperties = createAsyncThunk(
  "properties/fetchProperties",
  async (params: PropertySearchParams, { rejectWithValue }) => {
    try {
      const response = await propertiesApi.getProperties(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch properties"
      );
    }
  }
);

export const fetchFeaturedProperties = createAsyncThunk(
  "properties/fetchFeaturedProperties",
  async (_, { rejectWithValue }) => {
    try {
      const response = await propertiesApi.getFeaturedProperties();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch featured properties"
      );
    }
  }
);

export const fetchPropertyById = createAsyncThunk(
  "properties/fetchPropertyById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await propertiesApi.getPropertyById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch property"
      );
    }
  }
);

export const searchProperties = createAsyncThunk(
  "properties/searchProperties",
  async (params: PropertySearchParams, { rejectWithValue }) => {
    try {
      const response = await propertiesApi.searchProperties(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Search failed");
    }
  }
);

export const createProperty = createAsyncThunk(
  "properties/createProperty",
  async (propertyData: any, { rejectWithValue }) => {
    try {
      const response = await propertiesApi.createProperty(propertyData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create property"
      );
    }
  }
);

export const updateProperty = createAsyncThunk(
  "properties/updateProperty",
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await propertiesApi.updateProperty(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update property"
      );
    }
  }
);

export const deleteProperty = createAsyncThunk(
  "properties/deleteProperty",
  async (id: string, { rejectWithValue }) => {
    try {
      await propertiesApi.deleteProperty(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete property"
      );
    }
  }
);

export const saveProperty = createAsyncThunk(
  "properties/saveProperty",
  async (propertyId: string, { rejectWithValue }) => {
    try {
      const response = await propertiesApi.saveProperty(propertyId);
      return { propertyId, message: response.message };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to save property"
      );
    }
  }
);

export const unsaveProperty = createAsyncThunk(
  "properties/unsaveProperty",
  async (propertyId: string, { rejectWithValue }) => {
    try {
      const response = await propertiesApi.unsaveProperty(propertyId);
      return { propertyId, message: response.message };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to unsave property"
      );
    }
  }
);

export const fetchSavedProperties = createAsyncThunk(
  "properties/fetchSavedProperties",
  async (_, { rejectWithValue }) => {
    try {
      const response = await propertiesApi.getSavedProperties();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch saved properties"
      );
    }
  }
);

const propertySlice = createSlice({
  name: "properties",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<PropertyFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.searchParams.page = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchParams.page = 1;
    },
    setSearchParams: (
      state,
      action: PayloadAction<Partial<PropertySearchParams>>
    ) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentProperty: (state) => {
      state.currentProperty = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.searchParams.page = action.payload;
    },
    clearSavedProperties: (state) => {
      state.savedProperties = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch properties
      .addCase(fetchProperties.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProperties.fulfilled, (state, action) => {
        state.isLoading = false;
        state.properties = action.payload.properties;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
        state.error = null;
      })
      .addCase(fetchProperties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch featured properties
      .addCase(fetchFeaturedProperties.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedProperties.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featuredProperties = action.payload;
        state.error = null;
      })
      .addCase(fetchFeaturedProperties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch property by ID
      .addCase(fetchPropertyById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPropertyById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProperty = action.payload;
        state.error = null;
      })
      .addCase(fetchPropertyById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Search properties
      .addCase(searchProperties.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchProperties.fulfilled, (state, action) => {
        state.isLoading = false;
        state.properties = action.payload.properties;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
        state.error = null;
      })
      .addCase(searchProperties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create property
      .addCase(createProperty.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProperty.fulfilled, (state, action) => {
        state.isLoading = false;
        state.properties.unshift(action.payload);
        state.error = null;
      })
      .addCase(createProperty.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update property
      .addCase(updateProperty.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProperty.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.properties.findIndex(
          (p) => p._id === action.payload._id
        );
        if (index !== -1) {
          state.properties[index] = action.payload;
        }
        if (state.currentProperty?._id === action.payload._id) {
          state.currentProperty = action.payload;
        }
        state.error = null;
      })
      .addCase(updateProperty.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete property
      .addCase(deleteProperty.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProperty.fulfilled, (state, action) => {
        state.isLoading = false;
        state.properties = state.properties.filter(
          (p) => p._id !== action.payload
        );
        if (state.currentProperty?._id === action.payload) {
          state.currentProperty = null;
        }
        state.error = null;
      })
      .addCase(deleteProperty.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Save property
      .addCase(saveProperty.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveProperty.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Add property to saved properties if not already there
        const propertyExists = state.savedProperties.some(
          (p) => p._id === action.payload.propertyId
        );
        if (!propertyExists) {
          const property = state.properties.find(
            (p) => p._id === action.payload.propertyId
          );
          if (property) {
            state.savedProperties.push(property);
          }
        }
      })
      .addCase(saveProperty.rejected, (state, action) => {
        state.isLoading = false;
        // If property already saved, treat it as success and add to saved properties
        if (action.payload === "Property already saved") {
          const propertyId = action.meta.arg; // Get propertyId from the action argument
          const propertyExists = state.savedProperties.some(
            (p) => p._id === propertyId
          );
          if (!propertyExists) {
            const property =
              state.properties.find((p) => p._id === propertyId) ||
              state.currentProperty;
            if (property && property._id === propertyId) {
              state.savedProperties.push(property);
            }
          }
          state.error = null; // Clear error for "already saved" case
        } else {
          state.error = action.payload as string;
        }
      })

      // Unsave property
      .addCase(unsaveProperty.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(unsaveProperty.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Remove property from saved properties
        state.savedProperties = state.savedProperties.filter(
          (p) => p._id !== action.payload.propertyId
        );
      })
      .addCase(unsaveProperty.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch saved properties
      .addCase(fetchSavedProperties.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSavedProperties.fulfilled, (state, action) => {
        state.isLoading = false;
        state.savedProperties = action.payload;
        state.savedPropertiesLoaded = true;
        state.error = null;
      })
      .addCase(fetchSavedProperties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setSearchParams,
  clearError,
  clearCurrentProperty,
  setPage,
  clearSavedProperties,
} = propertySlice.actions;

export default propertySlice.reducer;
